import { forwardRef, useImperativeHandle } from "react";
import { BlockNoteEditor as BlockNoteEditorType, PartialBlock } from "@blocknote/core";
import { BlockNoteView } from "@blocknote/mantine";
import {
  BasicTextStyleButton,
  BlockTypeSelect,
  ColorStyleButton,
  FileCaptionButton,
  FileReplaceButton,
  FormattingToolbar,
  TextAlignButton,
  useCreateBlockNote,
} from "@blocknote/react";
import styled from "@emotion/styled";

import AddImageButton from "./custom_buttons/AddImageButton";
import CreateLinkButton from "./custom_buttons/CreateLinkButton";
import WordCountButton from "./custom_buttons/WordCountButton";
import { getCharacterCount, getWordCount, isBlockNoteEmpty } from "./utils";

import "@blocknote/mantine/style.css";

const DEFAULT_PARAGRAPH_PLACEHOLDER = "Start writing or use / to get a set of tools...";

/**
 * Props for the BlockNoteEditor component
 * @property initialContent - Initial content for the editor as an array of partial blocks
 * @property onChange - Callback function triggered when the editor content changes
 * @property editable - Whether the editor is in read-only mode (default: true)
 * @property minHeight - Minimum height of the editor (default: "400px")
 * @property sideMenu - Whether to show the side menu for block manipulation (default: false)
 * @property theme - Theme variant for the editor (default: "light")
 * @property staticFormattingToolbar - Whether to show a static formatting toolbar above the editor (default: false)
 * @property placeholder - Placeholder text for the main paragraph block (overrides the default)
 * @property uploadFile - Function to handle file uploads, should return a promise that resolves to the uploaded file URL
 */
export interface BlockNoteEditorProps {
  initialContent?: PartialBlock[];
  onChange?: (content: PartialBlock[]) => void;
  editable?: boolean;
  minHeight?: string | number;
  sideMenu?: boolean;
  theme?: "light" | "dark";
  staticFormattingToolbar?: boolean;
  placeholder?: string;
  uploadFile?: (file: File) => Promise<string>;
}

/**
 * Ref interface for accessing editor methods
 * @property getEditor - Get the current BlockNote editor instance
 * @property getContent - Get the current content as an array of partial blocks
 * @property clearContent - Clear all content from the editor
 * @property isEmpty - Check if the editor is empty
 * @property getWordCount - Get the word count of the editor content
 * @property getCharacterCount - Get the character count of the editor content
 */
export interface BlockNoteEditorRef {
  getEditor: () => BlockNoteEditorType | null;
  getContent: () => PartialBlock[];
  clearContent: () => void;
  isEmpty: () => boolean;
  getWordCount: () => number;
  getCharacterCount: () => number;
}

/**
 * BlockNoteEditor component - A rich text editor built on top of BlockNote
 *
 * BlockNote is a modern, block-based rich text editor that provides an intuitive
 * editing experience similar to Notion. It supports various block types including
 * headings, paragraphs, lists, and more.
 *
 * Features:
 * - Block-based editing with drag and drop support
 * - Rich text formatting (bold, italic, underline, etc.)
 * - Multiple block types (headings, lists, etc.)
 * - Light and dark theme support
 * - Read-only mode
 * - Customizable minimum height
 * - Optional side menu for block manipulation
 * - Ref support for programmatic control
 *
 * @example
 * ```tsx
 * // Basic usage
 * <BlockNoteEditor
 *   onChange={(content) => console.log('Content:', content)}
 * />
 *
 * // With initial content
 * <BlockNoteEditor
 *   initialContent={[
 *     { type: "heading", props: { level: 1 }, content: "Welcome" },
 *     { type: "paragraph", content: "Start writing..." }
 *   ]}
 *   theme="light"
 *   minHeight="500px"
 * />
 *
 * // With ref for programmatic control
 * const editorRef = useRef<BlockNoteEditorRef>(null);
 *
 * const handleSave = () => {
 *   const content = editorRef.current?.getContent();
 *   console.log('Saving:', content);
 * };
 *
 * <BlockNoteEditor ref={editorRef} />
 * ```
 *
 * Keyboard Shortcuts:
 * - Bold: Cmd/Ctrl + B
 * - Italic: Cmd/Ctrl + I
 * - Underline: Cmd/Ctrl + U
 * - Strikethrough: Cmd/Ctrl + Shift + S
 * - Undo: Cmd/Ctrl + Z
 * - Redo: Cmd/Ctrl + Shift + Z
 * - New block: Enter
 * - Convert to heading: Type # at start of line
 * - Convert to list: Type - or * at start of line
 *
 * @param props - The editor component props
 * @param ref - Ref for accessing editor methods
 */
export const BlockNoteEditor = forwardRef<BlockNoteEditorRef, BlockNoteEditorProps>(
  (
    {
      initialContent,
      onChange,
      editable = true,
      minHeight = "400px",
      sideMenu = false,
      theme = "light",
      staticFormattingToolbar = false,
      placeholder = DEFAULT_PARAGRAPH_PLACEHOLDER,
      uploadFile,
    },
    ref,
  ) => {
    const editor = useCreateBlockNote({
      initialContent,
      placeholders: {
        default: "",
      },
      uploadFile,
    });

    const isEmpty = isBlockNoteEmpty(editor.document);

    // Using useImperativeHandle to expose the editor instance and content methods
    useImperativeHandle(ref, () => ({
      getEditor: () => editor,
      getContent: () => editor.document,
      clearContent: () => {
        editor.replaceBlocks(editor.document, []);
      },
      isEmpty: () => isEmpty,
      getWordCount: () => getWordCount(editor.document),
      getCharacterCount: () => getCharacterCount(editor.document),
    }));

    const handleChange = () => {
      if (onChange) {
        onChange(editor.document);
      }
    };

    return (
      <EditorWrapper>
        {isEmpty && (
          <PlaceholderOverlay sideMenu={sideMenu} staticFormattingToolbar={staticFormattingToolbar}>
            {placeholder}
          </PlaceholderOverlay>
        )}
        <EditorContainer minHeight={minHeight} sideMenu={sideMenu}>
          <BlockNoteView
            editor={editor}
            theme={theme}
            sideMenu={sideMenu}
            editable={editable}
            formattingToolbar={!staticFormattingToolbar}
            onChange={handleChange}
            // We need this to be true to use the theme variables
            data-theming-css-variables-enabled="true"
          >
            {staticFormattingToolbar && (
              <FormattingToolbar>
                <BlockTypeSelect key="blockTypeSelect" />
                <FileCaptionButton key="fileCaptionButton" />
                <FileReplaceButton key="replaceFileButton" />

                <BasicTextStyleButton basicTextStyle="bold" key="boldStyleButton" />
                <BasicTextStyleButton basicTextStyle="italic" key="italicStyleButton" />
                <BasicTextStyleButton basicTextStyle="underline" key="underlineStyleButton" />

                <TextAlignButton textAlignment="left" key="textAlignLeftButton" />
                <TextAlignButton textAlignment="center" key="textAlignCenterButton" />
                <TextAlignButton textAlignment="right" key="textAlignRightButton" />

                <ColorStyleButton key="colorStyleButton" />
                <AddImageButton key="addImageButton" />
                <CreateLinkButton key="createLinkButton" />
                <ToolbarDivider />
                <WordCountButton key="wordCountButton" />
              </FormattingToolbar>
            )}
          </BlockNoteView>
        </EditorContainer>
      </EditorWrapper>
    );
  },
);

const EditorContainer = styled.div<{ minHeight?: string | number; sideMenu: boolean }>(
  ({ minHeight, sideMenu, theme }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",

    ".bn-container": {
      display: "flex",
      flexDirection: "column-reverse",
      gap: 8,
      minHeight: minHeight
        ? typeof minHeight === "number"
          ? `${minHeight}px`
          : minHeight
        : "auto",

      "--bn-colors-editor-text": theme.colors.sysTextDefault,
      "--bn-colors-editor-background": theme.colors.sysBackgroundDefault,
      "--bn-colors-menu-text": theme.colors.sysTextDefault,
      "--bn-colors-menu-background": theme.colors.sysBackgroundDefault,
      "--bn-colors-tooltip-text": theme.colors.sysTextDefault,
      "--bn-colors-tooltip-background": theme.colors.sysBackgroundInactive,
      "--bn-colors-hovered-text": theme.colors.sysTextDefault,
      "--bn-colors-hovered-background": theme.colors.sysBackgroundInactive,
      "--bn-colors-selected-text": theme.colors.sysTextWhite,
      "--bn-colors-selected-background": theme.colors.sysTextDefault,
      "--bn-colors-disabled-text": theme.colors.sysTextInactive,
      "--bn-colors-disabled-background": theme.colors.sysBackgroundInactive,
      "--bn-colors-shadow": theme.colors.sysBorderDecorative,
      "--bn-colors-border": theme.colors.sysBorderDecorative,
      "--bn-colors-side-menu": theme.colors.sysBorderDecorative,

      "--bn-colors-highlights-gray-text": theme.colors.sysTextSecondary,
      "--bn-colors-highlights-gray-background": theme.colors.sysBackgroundInactive,
      "--bn-colors-highlights-red-text": theme.colors.sysDestructiveDefault,
      "--bn-colors-highlights-red-background": theme.colors.sysDestructiveSubtleAlt,
      "--bn-colors-highlights-yellow-text": theme.colors.sysWarningDefault,
      "--bn-colors-highlights-yellow-background": theme.colors.sysWarningSubtleAlt,
      "--bn-colors-highlights-green-text": theme.colors.sysSuccessDefault,
      "--bn-colors-highlights-green-background": theme.colors.sysSuccessSubtleAlt,
      "--bn-colors-highlights-blue-text": theme.colors.sysInfoDefault,
      "--bn-colors-highlights-blue-background": theme.colors.sysInfoSubtleAlt,
      "--bn-colors-highlights-purple-text": theme.colors.sysBrandPurpleDefault,
      "--bn-colors-highlights-purple-background": theme.colors.sysSignalBuyer,
    },

    ".bn-editor": {
      padding: sideMenu ? "0 0 0 48px" : "0",
      flex: 1,
      fontFamily: "inherit",
    },

    ".bn-editor:focus-within": {
      outline: "none",
    },

    ".bn-formatting-toolbar": {
      marginInline: "auto",
      marginBottom: 16,
      display: "flex",
      justifyContent: "center",
      width: "100%",
      boxShadow: "none",
      flexWrap: "wrap",
    },
  }),
);

const EditorWrapper = styled.div({
  position: "relative",
  width: "100%",
});

const PlaceholderOverlay = styled.div<{ sideMenu: boolean; staticFormattingToolbar: boolean }>(
  ({ sideMenu, staticFormattingToolbar, theme }) => ({
    position: "absolute",
    top: staticFormattingToolbar ? 62 : 2,
    left: sideMenu ? 48 : 0,
    pointerEvents: "none",
    color: theme.colors.sysTextPlaceholder,
    fontSize: "17px",
    zIndex: 1,
  }),
);

const ToolbarDivider = styled.div(({ theme }) => ({
  width: "1px",
  backgroundColor: theme.colors.sysBorderDecorative,
  margin: "4px 8px",
}));

BlockNoteEditor.displayName = "BlockNoteEditor";
